import React, { useCallback, useMemo } from 'react';
import { Box, Collapse, Grid, InputBase, Tooltip } from '@mui/material';
import { Controller, ControllerRenderProps, useFormContext } from 'react-hook-form';
import { FormFields } from 'utils/types';
import { useFieldProps } from 'hooks';
import { useSourceClinicalMeetingTypes, useSourceEmployees } from 'components/hooks';
import { apiUserPatientProfileSubscriptions } from 'services/user-patient-profile-subscriptions';
import { SubscriptionInfo } from '../subscription-info';
import { SelectPatients } from 'components/select-patients';
import { AppCheckbox } from 'components/app-checkbox';
import { MEETING_TYPE_KEY } from 'services/clinical-meeting-types';
import { Stack } from 'components/stack';
import { useIsBelowMd } from 'hooks/use-is-below-md';
import { useTranslate } from 'hooks/use-translate';
import { EmployeeMeetingDateSelect } from 'components/employee-meeting-date-select';
import { SelectEmployee } from 'components/select-employee';
import { validateRule } from 'AurionCR/components/formV2';
import { PERMISSION_IDS } from 'services/user-employee-profile-permissions';
import { AppSelect } from 'components/app-select';
import { AppInput } from 'components/app-input';
import { ClinicalMeetingFormModel, ClinicalMeetingMeta } from '../../models';

import { rulesMeetingDateRange } from '../../helpers';
import { useClientMeetingDefaultDuration } from '../../hooks';
import style from './index.module.scss';

const useGetSubscriptionInfo =
  apiUserPatientProfileSubscriptions.useGetLatestPatientSubscriptionQuery;
interface Props {
  isLoading: boolean;
  isReadOnly: boolean;
  meetingTypeKey?: MEETING_TYPE_KEY;
  times: { id: string }[];
  meta: ClinicalMeetingMeta;
}

export const ClinicalMeetingFormEdit: React.FC<React.PropsWithChildren<Props>> = ({
  isLoading,
  isReadOnly,
  meetingTypeKey,
  meta,
}) => {
  const { t } = useTranslate();
  // N3 (T1): below md the SMS checkbox names itself -- its tooltip is a hover
  const isBelowMd = useIsBelowMd();

  const {
    control,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useFormContext<FormFields<ClinicalMeetingFormModel>>();

  const getFieldProps = useFieldProps({ errors });

  const date = watch('date');
  const clinicalMeetingSubjectID = watch('clinicalMeetingSubjectID');

  const sourceMeetingTypes = useSourceClinicalMeetingTypes();
  const meetingTypesOptions = useMemo(() => {
    return meetingTypeKey
      ? sourceMeetingTypes.data.filter((item) => item.meetingTypeKey === meetingTypeKey)
      : sourceMeetingTypes.data;
  }, [sourceMeetingTypes.data, meetingTypeKey]);

  const userPatientProfileID = watch('userPatientProfileID');

  const userEmployeeProfileID = watch('userEmployeeProfileID');

  const { data: userPatientSubscription } = useGetSubscriptionInfo(userPatientProfileID, {
    skip: !userPatientProfileID,
  });

  const renderSendSms = useCallback(
    ({
      field: renderProps,
    }: {
      field: ControllerRenderProps<FormFields<ClinicalMeetingFormModel>>;
    }) => {
      return (
        <Tooltip title={t('send-sms')}>
          <Box
            sx={{
              mt: 1.5,
            }}
          >
            <AppCheckbox
              {...getFieldProps(renderProps)}
              label={isBelowMd ? t('send-sms') : null}
              disabled={isLoading || isReadOnly}
            />
          </Box>
        </Tooltip>
      );
    },
    [getFieldProps, isLoading, isReadOnly, t, isBelowMd],
  );

  const sourceEmployees = useSourceEmployees();
  const optionsEmployees = useMemo(() => {
    // only doctors and dietitians
    return sourceEmployees.data.filter((employee) => {
      return [PERMISSION_IDS.DIETITIAN, PERMISSION_IDS.DOCTOR].some(
        (permissionID) => employee.userEmployeeProfilePermissionID === permissionID,
      );
    });
  }, [sourceEmployees.data]);

  const rulesMeeting = useMemo(() => {
    return {
      required: {
        value: true,
        message: 'rule-required',
      },
      ...rulesMeetingDateRange,
    };
  }, []);

  const getDefaultDuration = useClientMeetingDefaultDuration(clinicalMeetingSubjectID);

  return (
    <Grid container spacing={2}>
      <Controller
        name="date"
        control={control}
        render={({ field }) => <InputBase {...field} type="hidden" />}
      />
      <Grid size={12}>
        <Controller
          control={control}
          name={'clinicalMeetingTypeID'}
          rules={validateRule('required')}
          render={({ field: renderProps }) => {
            return (
              <AppSelect
                {...getFieldProps(renderProps)}
                label={t('meeting-type')}
                disabled={isLoading || isReadOnly}
                options={meetingTypesOptions}
                loading={sourceMeetingTypes.loading}
                disableClearable
              />
            );
          }}
        />
      </Grid>
      <Grid size={12}>
        <Box sx={{ bgcolor: '#F6F8F8', mt: 1, px: 1, mx: -1, borderRadius: 6, overflow: 'hidden' }}>
          <Controller
            control={control}
            name={'userPatientProfileID'}
            rules={validateRule('required')}
            render={({ field: renderProps }) => {
              return (
                <SelectPatients
                  {...getFieldProps(renderProps)}
                  disabled={isLoading || isReadOnly || meta.disablePatient}
                  disableClearable
                />
              );
            }}
          />
        </Box>
      </Grid>
      {userPatientSubscription && (
        <Grid size={12}>
          <SubscriptionInfo
            sx={{ pt: 1.1, pb: 1.4 }}
            label={t('subscription')}
            title={userPatientSubscription.title || ''}
            start={userPatientSubscription.startDate}
            end={userPatientSubscription.endDate}
          />
        </Grid>
      )}

      <Grid size={12}>
        <Collapse in={Boolean(userPatientProfileID)}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Stack
                className={style.fieldsRow}
                spacing={2}
                sx={{
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                  }}
                >
                  <Controller
                    control={control}
                    name={'userEmployeeProfileID'}
                    rules={validateRule('required')}
                    render={({ field: renderProps }) => {
                      return (
                        <SelectEmployee
                          {...getFieldProps(renderProps)}
                          options={optionsEmployees}
                          fullWidth={true}
                          disabled={isLoading || isReadOnly}
                          label={t('user-employee-profile')}
                          onChange={(e) => {
                            setValue('meetingDateTimeUserEmployeeProfile', null);
                            renderProps.onChange(e);
                          }}
                        />
                      );
                    }}
                  />
                </Box>
                <div>
                  <Controller
                    control={control}
                    name={'meetingDateTimeUserEmployeeProfile'}
                    rules={rulesMeeting as any} // RHF 7: a rules object shared by several fields cannot satisfy each field's own RegisterOptions<Model, Name>; same runtime.
                    render={({ field: renderProps }) => {
                      return (
                        <EmployeeMeetingDateSelect
                          {...getFieldProps(renderProps)}
                          subscription={userPatientSubscription ?? null}
                          disabled={isLoading || isReadOnly || !userEmployeeProfileID}
                          userEmployeeID={userEmployeeProfileID}
                          preferStartDate={date}
                          onChange={(value) => {
                            renderProps.onChange(value);
                            trigger();
                          }}
                          defaultDuration={getDefaultDuration(userEmployeeProfileID)}
                        />
                      );
                    }}
                  />
                </div>
                <Controller control={control} name={'isSendSmsEmployee'} render={renderSendSms} />
              </Stack>
            </Grid>
          </Grid>
        </Collapse>
      </Grid>
      <Grid size={12}>
        <Controller
          control={control}
          name={'organizerUserEmployeeProfileID'}
          rules={validateRule('required')}
          render={({ field: renderProps }) => (
            <SelectEmployee {...getFieldProps(renderProps)} disableClearable />
          )}
        />
        <Controller
          control={control}
          name={'remarks'}
          render={({ field: renderProps }) => (
            <AppInput {...getFieldProps(renderProps)} multiline minRows={2} />
          )}
        />
      </Grid>
    </Grid>
  );
};
